import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Radar,
  MapPinned,
  ClipboardCheck,
  FlaskConical,
  FileText,
  FileStack,
  ReceiptText,
  CheckSquare,
  Truck,
  Boxes,
  Target,
  Trophy,
  LifeBuoy,
  AlertTriangle,
  Users,
  ClipboardList,
  MessagesSquare,
  LineChart,
  Settings,
  CalendarClock,
  Wallet,
  ShoppingCart,
  CalendarRange,
} from 'lucide-react';
import { cn } from '@/shared/utils/utils';

/**
 * ZynxIcon — Enterprise design system icon component.
 *
 * Backed 100% by official, optically balanced Lucide vector glyphs
 * at standard 24px grid with consistent 1.75–2.0 stroke geometry.
 *
 * Variants:
 *  - `bare` (default): Pure unboxed vector glyph with text-current inherit.
 *  - `flat`: Subtle, elegant tinted chip for toolbars and badges.
 *  - `tile`: Soft rounded-xl badge with subtle tone borders for KPI headers.
 */

export type ZynxIconName =
  | 'dashboard' | 'radar' | 'visit' | 'dsr' | 'trial' | 'quote' | 'docs'
  | 'invoice' | 'approve' | 'challan' | 'stock' | 'target' | 'reward'
  | 'support' | 'alert' | 'pipeline' | 'contacts' | 'tasks' | 'chat'
  | 'analytics' | 'settings' | 'attendance' | 'payroll' | 'orders'
  | 'leaves' | 'fleet';

export type ZynxTone =
  | 'indigo' | 'fuchsia' | 'sky' | 'blue' | 'teal' | 'violet' | 'emerald'
  | 'lime' | 'orange' | 'amber' | 'rose' | 'pink' | 'red';

export const ZYNX_LUCIDE_MAP: Record<ZynxIconName, LucideIcon> = {
  dashboard: LayoutDashboard,
  radar: Radar,
  visit: MapPinned,
  dsr: ClipboardCheck,
  trial: FlaskConical,
  quote: FileText,
  docs: FileStack,
  invoice: ReceiptText,
  approve: CheckSquare,
  challan: Truck,
  stock: Boxes,
  target: Target,
  reward: Trophy,
  support: LifeBuoy,
  alert: AlertTriangle,
  pipeline: Target,
  contacts: Users,
  tasks: ClipboardList,
  chat: MessagesSquare,
  analytics: LineChart,
  settings: Settings,
  attendance: CalendarClock,
  payroll: Wallet,
  orders: ShoppingCart,
  leaves: CalendarRange,
  fleet: Truck,
};

const TONE_BADGE: Record<ZynxTone, string> = {
  indigo: 'bg-primary/10 text-primary border-primary/15',
  fuchsia: 'bg-fuchsia-500/10 text-fuchsia-700 border-fuchsia-500/20',
  sky: 'bg-sky-500/10 text-sky-700 border-sky-500/20',
  blue: 'bg-blue-500/10 text-blue-700 border-blue-500/20',
  teal: 'bg-teal-500/10 text-teal-700 border-teal-500/20',
  violet: 'bg-violet-500/10 text-violet-700 border-violet-500/20',
  emerald: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20',
  lime: 'bg-lime-500/10 text-lime-700 border-lime-500/20',
  orange: 'bg-orange-500/10 text-orange-700 border-orange-500/20',
  amber: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  rose: 'bg-rose-500/10 text-rose-700 border-rose-500/20',
  pink: 'bg-pink-500/10 text-pink-700 border-pink-500/20',
  red: 'bg-red-500/10 text-red-700 border-red-500/20',
};

export interface ZynxIconProps {
  name?: ZynxIconName;
  icon?: LucideIcon;
  variant?: 'tile' | 'flat' | 'bare';
  tone?: ZynxTone;
  size?: number;
  className?: string;
  label?: string;
}

export function ZynxIcon({
  name,
  icon: IconProp,
  variant = 'bare',
  tone = 'indigo',
  size = 20,
  className,
  label,
}: ZynxIconProps) {
  const IconComponent = (name && ZYNX_LUCIDE_MAP[name]) || IconProp;
  if (!IconComponent) return null;

  const innerSize = variant === 'bare' ? size : Math.round(size * 0.58);

  const glyph = (
    <IconComponent
      style={{ width: innerSize, height: innerSize }}
      strokeWidth={1.75}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
      className="shrink-0"
    />
  );

  if (variant === 'bare') {
    return (
      <span className={cn('inline-flex shrink-0 items-center justify-center text-current', className)}>
        {glyph}
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-xl border transition-all duration-150',
        TONE_BADGE[tone] || TONE_BADGE.indigo,
        className,
      )}
      style={{ width: size, height: size }}
    >
      {glyph}
    </span>
  );
}
